import { KeyboardEvent, ReactNode, useEffect, useRef } from 'react'
import classes from './ChatWidget.module.scss'
import { IconClose } from './icons'

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

interface Props {
  titleId: string
  title: string
  description: string
  onClose: () => void
  busy?: boolean
  children: ReactNode
}

/** Chat panelinin içinde çalışan, odağı kendi sınırlarında tutan ortak modal kabuğu. */
export default function FeatureModal({ titleId, title, description, onClose, busy, children }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const initialFocus =
      dialogRef.current?.querySelector<HTMLElement>('[data-modal-initial-focus]') ??
      dialogRef.current?.querySelector<HTMLElement>('input, select, button')
    initialFocus?.focus()

    return () => previouslyFocused?.focus()
  }, [])

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      onClose()
      return
    }

    if (event.key !== 'Tab' || !dialogRef.current) return

    const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      element => element.offsetParent !== null
    )
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <div
      className={classes.featureModalOverlay}
      onMouseDown={event => {
        if (event.currentTarget === event.target) onClose()
      }}
    >
      <div
        ref={dialogRef}
        className={classes.featureModal}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        onKeyDown={handleKeyDown}
      >
        <div className={classes.featureModalHeader}>
          <div>
            <h2 id={titleId} className={classes.featureModalTitle}>
              {title}
            </h2>
            <p className={classes.featureModalDescription}>{description}</p>
          </div>
          <button
            type='button'
            className={classes.featureModalClose}
            onClick={onClose}
            aria-label={`${title} penceresini kapat`}
          >
            <IconClose />
          </button>
        </div>

        <div className={classes.featureModalBody}>{children}</div>
      </div>
    </div>
  )
}

interface FeedbackProps {
  tone: 'error' | 'empty' | 'info'
  children: ReactNode
}

export function ModalFeedback({ tone, children }: FeedbackProps) {
  const className = [
    classes.modalFeedback,
    tone === 'error'
      ? classes.modalFeedbackError
      : tone === 'empty'
      ? classes.modalFeedbackEmpty
      : classes.modalFeedbackInfo,
  ].join(' ')

  return (
    <div className={className} role={tone === 'error' ? 'alert' : 'status'} aria-live='polite'>
      {children}
    </div>
  )
}

export function ModalLoading({ label }: { label: string }) {
  return (
    <div className={classes.modalLoading} role='status' aria-live='polite'>
      <span className={classes.modalLoadingSpinner} aria-hidden='true' />
      <span>{label}</span>
    </div>
  )
}
